import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Layers,
  X,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  AlertTriangle,
  Info,
  Check,
} from 'lucide-react';
import {
  useBackgroundStore,
  makeLayer,
} from './BackgroundProvider';
import BGMiniPreview from './BGMiniPreview';
import { uploadLayerFile, isIdbSrc, idbKey, resolveToUrl, oversizeMessage, MAX_UPLOAD_LABEL } from './mediaStore';
import { toast } from "../../utils/toast";
import {
  Label,
  Input,
  Select,
  Slider,
  PrimaryButton,
  SecondaryButton,
} from './Primitives';

const MAX = 6;
const MIN = 2;

export const SCENE_TEMPLATES = [
  {
    name: 'Sky · Mountains · Trees',
    settings: { dim: 10, overlayAlpha: 0, parallax: 100 },
    layers: [
      {
        src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=2400&q=80',
        depth: 10, scale: 115, blur: 2, opacity: 100, blend: 'normal',
        visible: true, offsetX: 0, offsetY: 0, name: 'Sky',
      },
      {
        src: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=2400&q=80',
        depth: 50, scale: 110, blur: 0, opacity: 85, blend: 'multiply',
        visible: true, offsetX: 0, offsetY: 20, name: 'Mountains',
      },
      {
        src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=2400&q=80',
        depth: 95, scale: 115, blur: 0, opacity: 70, blend: 'multiply',
        visible: true, offsetX: 0, offsetY: 40, name: 'Trees',
      },
    ],
  },
  {
    name: 'Stars · Moon · Clouds',
    settings: { dim: 25, overlayColor: '#030213', overlayAlpha: 10, parallax: 110 },
    layers: [
      {
        src: 'linear-gradient(180deg, #030213 0%, #1e1b4b 60%, #312e81 100%)',
        depth: 5, scale: 110, blur: 0, opacity: 100, blend: 'normal',
        visible: true, offsetX: 0, offsetY: 0, name: 'Night gradient',
      },
      {
        src: 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=2400&q=80',
        depth: 25, scale: 115, blur: 0, opacity: 70, blend: 'screen',
        visible: true, offsetX: 0, offsetY: 0, name: 'Stars',
      },
      {
        src: 'https://images.unsplash.com/photo-1532693322450-2cb5c511067d?w=2400&q=80',
        depth: 60, scale: 105, blur: 0, opacity: 85, blend: 'screen',
        visible: true, offsetX: 0, offsetY: -30, name: 'Moon',
      },
      {
        src: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=2400&q=80',
        depth: 95, scale: 120, blur: 2, opacity: 55, blend: 'screen',
        visible: true, offsetX: 0, offsetY: 40, name: 'Clouds',
      },
    ],
  },
];

export default function SceneBuilder({ existing, onClose }) {
  const { addBackground, updateBackground, resolveSrc } = useBackgroundStore();

  const makeFromExisting = () => {
    if (!existing)
      return {
        id: 'scene-' + Date.now(),
        name: 'Untitled Scene',
        kind: 'scene',
        type: 'image',
        tags: ['scene', 'uploads'],
        priority: 40,
        schedule: { kind: 'library' },
        settings: {
          blur: 0, dim: 15, parallax: 100,
          overlayColor: '#0a0a0a', overlayAlpha: 0, scale: 100,
        },
        layers: [],
        src: null,
        thumb: null,
      };
    return JSON.parse(JSON.stringify({ ...existing, kind: 'scene' }));
  };

  const [draft, setDraft] = useState(makeFromExisting);
  const [selectedId, setSelectedId] = useState(existing?.layers?.[0]?.id || null);
  const [jpgWarned, setJpgWarned] = useState(false);
  const fileRef = useRef(null);

  // A layer added in this modal exists only in `draft`. The provider's
  // `resolveSrc` reads a mediaUrls map it builds from the SAVED library, so an
  // `indexeddb:` key that isn't in a stored background yet resolves to null —
  // which is why a freshly uploaded layer rendered as an empty grey box in both
  // the preview and its thumbnail. Resolve the draft's own keys here; the blob
  // is already in IDB by the time the layer is pushed into state.
  const resolvedRef = useRef({});
  const [draftUrls, setDraftUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    const keys = [
      ...new Set(
        draft.layers
          .map((l) => l.src)
          .filter(isIdbSrc)
          .map(idbKey)
      ),
    ];
    const missing = keys.filter((k) => !resolvedRef.current[k]);
    if (!missing.length) return undefined;
    (async () => {
      const next = {};
      await Promise.all(
        missing.map(async (k) => {
          // One unreadable layer must not blank out the rest of the scene.
          try {
            const url = await resolveToUrl(k);
            if (url) next[k] = url;
          } catch (e) {
            console.warn('[scene] could not resolve layer media', k, e?.message);
          }
        })
      );
      if (cancelled || !Object.keys(next).length) return;
      resolvedRef.current = { ...resolvedRef.current, ...next };
      setDraftUrls(resolvedRef.current);
    })();
    return () => {
      cancelled = true;
    };
  }, [draft.layers]);

  // Draft-local keys first, then the provider (saved scenes, "/uploads/..."
  // paths and the Unsplash template seeds all come back through it).
  const resolveDraftSrc = useCallback(
    (src) => {
      if (isIdbSrc(src)) return draftUrls[idbKey(src)] || resolveSrc(src);
      return resolveSrc(src);
    },
    [draftUrls, resolveSrc]
  );

  const patchDraft = (p) => setDraft((d) => ({ ...d, ...p }));
  const patchSettings = (p) =>
    setDraft((d) => ({ ...d, settings: { ...d.settings, ...p } }));
  const patchLayer = (id, p) =>
    setDraft((d) => ({
      ...d,
      layers: d.layers.map((l) => (l.id === id ? { ...l, ...p } : l)),
    }));
  const selected = draft.layers.find((l) => l.id === selectedId) || null;

  const handleFiles = async (files) => {
    const images = [...files].filter((f) => f.type.startsWith('image/'));
    // 10 MB per image: name each one that's over, and add the rest.
    const oversized = images.filter((f) => oversizeMessage(f));
    if (oversized.length === 1) toast.error(oversizeMessage(oversized[0]));
    else if (oversized.length > 1) {
      toast.error(
        `${oversized.length} images are over the ${MAX_UPLOAD_LABEL} limit and weren't added: ` +
          oversized.map((f) => `"${f.name}"`).join(', ')
      );
    }
    const imgs = images.filter((f) => !oversizeMessage(f));
    if (!imgs.length) return;
    const hasJpg = imgs.some((f) => /jpe?g/i.test(f.type));
    if (hasJpg) setJpgWarned(true);
    const slots = MAX - draft.layers.length;
    const picks = imgs.slice(0, slots);
    // Upload each file to IndexedDB; draft only keeps `indexeddb:<key>` refs,
    // so the full state stays well under the localStorage quota.
    for (let i = 0; i < picks.length; i += 1) {
      const f = picks[i];
      try {
        const { src } = await uploadLayerFile(f);
        const depth = Math.min(20 + (draft.layers.length + i) * 25, 100);
        const newLayer = makeLayer(src, {
          depth, scale: 100, blur: 0, opacity: 100,
          blend: 'normal', visible: true, offsetX: 0, offsetY: 0,
        });
        newLayer.name = f.name.replace(/\.[^.]+$/, '');
        newLayer.mime = f.type;
        setDraft((d) => ({ ...d, layers: [...d.layers, newLayer] }));
        setSelectedId(newLayer.id);
      } catch (err) {
        console.error('[scene] layer upload failed', err);
        // eslint-disable-next-line no-alert
        toast.error(err?.message || 'Could not add this image.');
      }
    }
  };

  const moveLayer = (id, dir) => {
    setDraft((d) => {
      const idx = d.layers.findIndex((l) => l.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= d.layers.length) return d;
      const layers = [...d.layers];
      [layers[idx], layers[j]] = [layers[j], layers[idx]];
      return { ...d, layers };
    });
  };
  const removeLayer = (id) =>
    setDraft((d) => ({ ...d, layers: d.layers.filter((l) => l.id !== id) }));

  const loadTemplate = (tpl) => {
    setDraft((d) => ({
      ...d,
      name: d.name === 'Untitled Scene' ? tpl.name : d.name,
      layers: tpl.layers.map((L) => makeLayer(L.src, { ...L })),
      settings: { ...d.settings, ...(tpl.settings || {}) },
    }));
    setSelectedId(null);
  };

  const canSave = draft.layers.length >= MIN;

  const save = () => {
    const firstImg = draft.layers.find(
      (l) =>
        !String(l.src).startsWith('linear-gradient') &&
        !String(l.src).startsWith('#')
    );
    const thumb = firstImg?.src || draft.layers[0]?.src;
    const finalDraft = {
      ...draft,
      kind: 'scene',
      type: 'image',
      src: thumb,
      thumb,
    };
    if (existing) updateBackground(existing.id, finalDraft);
    else addBackground(finalDraft);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1280px, 100%)',
          height: 'min(860px, 100%)',
          background: '#fff',
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 50px -20px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 22px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'linear-gradient(135deg,#ff6900,#f54900)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px -4px rgba(245,73,0,0.4)',
            }}
          >
            <Layers size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: '700 18px Inter',
                color: '#0a0a0a',
                letterSpacing: '-0.02em',
              }}
            >
              {existing ? 'Edit scene' : 'Create a scene'}
            </div>
            <div style={{ font: '400 12px Inter', color: '#4a5565' }}>
              Stack 2–6 images with custom depth for true parallax
            </div>
          </div>
          <Input
            value={draft.name}
            onChange={(e) => patchDraft({ name: e.target.value })}
            style={{ width: 260, maxWidth: '28vw' }}
          />
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              border: 0,
              background: '#f3f4f6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // The app-global `button { padding: 0 16px }` (index.css) plus a
              // border-box reset left this 34px button a 2px content box, which
              // squeezed the 16px icon down to an invisible sliver. Every other
              // icon button here sets its own padding; this one has to too.
              padding: 0,
              flexShrink: 0,
            }}
          >
            <X size={16} style={{ flexShrink: 0 }} />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '260px 1fr 320px',
            minHeight: 0,
          }}
        >
          {/* Layer stack */}
          <div
            style={{
              borderRight: '1px solid #e5e7eb',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <div style={{ font: '600 13px Inter', color: '#0a0a0a', flex: 1 }}>
                Layers
              </div>
              <span style={{ font: '500 11px Inter', color: '#6a7282' }}>
                {draft.layers.length}/{MAX}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {draft.layers.length === 0 && (
                <div
                  style={{
                    padding: '24px 12px',
                    textAlign: 'center',
                    color: '#6a7282',
                    font: '400 12px Inter',
                    border: '1px dashed #e5e7eb',
                    borderRadius: 10,
                  }}
                >
                  Upload 2–6 images to start.
                  <br />
                  Background first, foreground last.
                </div>
              )}
              {[...draft.layers].reverse().map((L, ri) => {
                const i = draft.layers.length - 1 - ri;
                // Turns an `indexeddb:` handle (draft-local or saved) and a
                // stored "/uploads/..." path into something this thumbnail can
                // show. Falling back to the raw `indexeddb:...` string would
                // just render as a broken url(), so keep it null-ish instead.
                const src = resolveDraftSrc(L.src);
                const isCss =
                  typeof src === 'string' &&
                  (src.startsWith('linear-gradient') || src.startsWith('#'));
                const isImg = typeof src === 'string' && !isCss;
                return (
                  <div
                    key={L.id}
                    onClick={() => setSelectedId(L.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 8,
                      borderRadius: 10,
                      cursor: 'pointer',
                      background: selectedId === L.id ? '#fff7ed' : '#fff',
                      border:
                        '1px solid ' +
                        (selectedId === L.id ? '#f54900' : '#e5e7eb'),
                      opacity: L.visible === false ? 0.5 : 1,
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 32,
                        borderRadius: 6,
                        // Bare checkerboard while the blob URL is still
                        // resolving (src null) — never url("indexeddb:...").
                        background: isCss
                          ? src
                          : (isImg ? `url("${src}") center/cover, ` : '') +
                            'repeating-conic-gradient(#e5e7eb 0% 25%, #fff 0% 50%) 0 0/10px 10px',
                        flexShrink: 0,
                        border: '1px solid #e5e7eb',
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          font: '500 12px Inter',
                          color: '#0a0a0a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Layer {i + 1}
                        {L.name ? ` · ${L.name}` : ''}
                      </div>
                      <div style={{ font: '400 10px Inter', color: '#6a7282' }}>
                        Depth {L.depth} · {L.blend}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        patchLayer(L.id, { visible: L.visible === false });
                      }}
                      style={{
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        padding: 4,
                        color: '#4a5565',
                      }}
                      title="Toggle visibility"
                    >
                      {L.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveLayer(L.id, +1);
                        }}
                        disabled={i === draft.layers.length - 1}
                        style={{
                          background: 'transparent',
                          border: 0,
                          cursor: 'pointer',
                          padding: '0 2px',
                          color: '#4a5565',
                          opacity: i === draft.layers.length - 1 ? 0.3 : 1,
                        }}
                        title="Bring forward"
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveLayer(L.id, -1);
                        }}
                        disabled={i === 0}
                        style={{
                          background: 'transparent',
                          border: 0,
                          cursor: 'pointer',
                          padding: '0 2px',
                          color: '#4a5565',
                          opacity: i === 0 ? 0.3 : 1,
                        }}
                        title="Send backward"
                      >
                        <ChevronDown size={12} />
                      </button>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLayer(L.id);
                        if (selectedId === L.id) setSelectedId(null);
                      }}
                      style={{
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        padding: 4,
                        color: '#e7000b',
                      }}
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                padding: '10px 14px',
                borderTop: '1px solid #f3f4f6',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <input
                ref={fileRef}
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.target.value = '';
                }}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              <PrimaryButton
                onClick={() => fileRef.current?.click()}
                disabled={draft.layers.length >= MAX}
                style={{ justifyContent: 'center' }}
              >
                <Plus size={16} /> Add image layer
              </PrimaryButton>
              <div style={{ textAlign: 'center', font: "400 11.5px 'Inter', sans-serif", color: '#6b7280' }}>
                Max {MAX_UPLOAD_LABEL} per image
              </div>
              {jpgWarned && (
                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    font: '400 11px Inter',
                    color: '#854d0e',
                    display: 'flex',
                    gap: 6,
                    alignItems: 'flex-start',
                  }}
                >
                  <AlertTriangle
                    size={14}
                    color="#b45309"
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <span>
                    <strong>Opaque JPG detected.</strong> For real depth, upper
                    layers should be PNG/WebP with transparent backgrounds.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div
            style={{
              background: '#f9fafb',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minHeight: 0 }}>
              <BGMiniPreview
                bg={draft}
                height="100%"
                showLabel={false}
                maxOffset={24}
                resolveSrc={resolveDraftSrc}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                background: '#fff',
                borderRadius: 10,
                border: '1px solid #e5e7eb',
              }}
            >
              <Info size={14} color="#2b7fff" />
              <span style={{ font: '400 12px Inter', color: '#4a5565', flex: 1 }}>
                Move cursor over the preview. Deeper layers (low depth) barely
                move; foreground layers (high depth) drift the most.
              </span>
              {draft.layers.length < MIN && (
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: 9999,
                    background: '#fef2f2',
                    color: '#e7000b',
                    font: '500 11px Inter',
                  }}
                >
                  Needs {MIN - draft.layers.length} more layer
                  {MIN - draft.layers.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div>
              <div
                style={{
                  font: '500 11px Inter',
                  color: '#6a7282',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 8,
                }}
              >
                Start from a template
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {SCENE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.name}
                    onClick={() => loadTemplate(tpl)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px 8px 8px',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      {tpl.layers.slice(0, 3).map((l, i) => (
                        <div
                          key={i}
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            marginLeft: i === 0 ? 0 : -8,
                            border: '2px solid #fff',
                            background:
                              String(l.src).startsWith('linear-gradient') ||
                              String(l.src).startsWith('#')
                                ? l.src
                                : `url("${resolveDraftSrc(l.src) || l.src}") center/cover`,
                          }}
                        />
                      ))}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ font: '600 12px Inter', color: '#0a0a0a' }}>
                        {tpl.name}
                      </div>
                      <div style={{ font: '400 10px Inter', color: '#6a7282' }}>
                        {tpl.layers.length} layers
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Inspector */}
          <div
            style={{
              borderLeft: '1px solid #e5e7eb',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid #f3f4f6',
                font: '600 13px Inter',
                color: '#0a0a0a',
              }}
            >
              {selected
                ? `Layer ${draft.layers.findIndex((l) => l.id === selected.id) + 1}`
                : 'Scene settings'}
            </div>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {selected ? (
                <>
                  <div>
                    <Label>
                      Depth <span style={{ color: '#99a1af' }}>{selected.depth}</span>{' '}
                      <span style={{ color: '#6a7282' }}>
                        {selected.depth <= 20
                          ? '· background'
                          : selected.depth >= 80
                          ? '· foreground'
                          : '· mid'}
                      </span>
                    </Label>
                    <Slider
                      value={selected.depth}
                      min={0}
                      max={100}
                      onChange={(v) => patchLayer(selected.id, { depth: v })}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        font: '400 10px Inter',
                        color: '#99a1af',
                        marginTop: 2,
                      }}
                    >
                      <span>Still</span>
                      <span>Drifting</span>
                    </div>
                  </div>
                  <div>
                    <Label>
                      Opacity{' '}
                      <span style={{ color: '#99a1af' }}>{selected.opacity}%</span>
                    </Label>
                    <Slider
                      value={selected.opacity}
                      min={0}
                      max={100}
                      onChange={(v) => patchLayer(selected.id, { opacity: v })}
                    />
                  </div>
                  <div>
                    <Label>
                      Blur <span style={{ color: '#99a1af' }}>{selected.blur}px</span>
                    </Label>
                    <Slider
                      value={selected.blur}
                      min={0}
                      max={30}
                      onChange={(v) => patchLayer(selected.id, { blur: v })}
                    />
                  </div>
                  <div>
                    <Label>
                      Scale <span style={{ color: '#99a1af' }}>{selected.scale}%</span>
                    </Label>
                    <Slider
                      value={selected.scale}
                      min={80}
                      max={150}
                      onChange={(v) => patchLayer(selected.id, { scale: v })}
                    />
                  </div>
                  <div>
                    <Label>
                      Rotation{' '}
                      <span style={{ color: '#99a1af' }}>{selected.rotate}°</span>
                    </Label>
                    <Slider
                      value={selected.rotate}
                      min={-45}
                      max={45}
                      onChange={(v) => patchLayer(selected.id, { rotate: v })}
                    />
                  </div>
                  <div>
                    <Label>Blend mode</Label>
                    <Select
                      value={selected.blend}
                      onChange={(e) =>
                        patchLayer(selected.id, { blend: e.target.value })
                      }
                    >
                      <option value="normal">Normal</option>
                      <option value="multiply">Multiply</option>
                      <option value="screen">Screen</option>
                      <option value="overlay">Overlay</option>
                    </Select>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 10,
                    }}
                  >
                    <div>
                      <Label>
                        Offset X{' '}
                        <span style={{ color: '#99a1af' }}>
                          {selected.offsetX || 0}px
                        </span>
                      </Label>
                      <Slider
                        value={selected.offsetX || 0}
                        min={-60}
                        max={60}
                        onChange={(v) => patchLayer(selected.id, { offsetX: v })}
                      />
                    </div>
                    <div>
                      <Label>
                        Offset Y{' '}
                        <span style={{ color: '#99a1af' }}>
                          {selected.offsetY || 0}px
                        </span>
                      </Label>
                      <Slider
                        value={selected.offsetY || 0}
                        min={-60}
                        max={60}
                        onChange={(v) => patchLayer(selected.id, { offsetY: v })}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>
                      Parallax intensity{' '}
                      <span style={{ color: '#99a1af' }}>
                        {draft.settings.parallax}%
                      </span>
                    </Label>
                    <Slider
                      value={draft.settings.parallax}
                      min={0}
                      max={200}
                      onChange={(v) => patchSettings({ parallax: v })}
                    />
                  </div>
                  <div>
                    <Label>
                      Dim{' '}
                      <span style={{ color: '#99a1af' }}>
                        {draft.settings.dim}%
                      </span>
                    </Label>
                    <Slider
                      value={draft.settings.dim}
                      min={0}
                      max={80}
                      onChange={(v) => patchSettings({ dim: v })}
                    />
                  </div>
                  <div>
                    <Label>Overlay color</Label>
                    <input
                      type="color"
                      value={draft.settings.overlayColor}
                      onChange={(e) =>
                        patchSettings({ overlayColor: e.target.value })
                      }
                      style={{
                        width: '100%',
                        height: 34,
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: 2,
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                  <div>
                    <Label>
                      Overlay opacity{' '}
                      <span style={{ color: '#99a1af' }}>
                        {draft.settings.overlayAlpha}%
                      </span>
                    </Label>
                    <Slider
                      value={draft.settings.overlayAlpha}
                      min={0}
                      max={80}
                      onChange={(v) => patchSettings({ overlayAlpha: v })}
                    />
                  </div>
                  <div
                    style={{
                      marginTop: 12,
                      padding: 10,
                      borderRadius: 8,
                      background: '#f9fafb',
                      border: '1px dashed #e5e7eb',
                      font: '400 11px Inter',
                      color: '#6a7282',
                    }}
                  >
                    Select a layer on the left to edit depth, blur, opacity,
                    scale, rotation, blend mode, and starting offset.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '12px 22px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div style={{ font: '400 12px Inter', color: '#4a5565', flex: 1 }}>
            {existing
              ? 'Changes save back to this scene.'
              : 'Scene will be added to your library.'}
          </div>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={save} disabled={!canSave}>
            <Check size={16} /> {existing ? 'Save changes' : 'Create scene'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
